/* eslint-disable */
import { createSlice } from "@reduxjs/toolkit";
// Utils
import { TIME_HORIZONS } from "utils/constants/time";
import { autoGranularity, datetimeToDate, today } from "utils/time";

const applicationSlice = createSlice({
  name: "application",
  initialState: {
    successAlert: { active: false, title: null, description: null },
    warningAlert: {
      active: false,
      title: null,
      text: null,
      confirmText: null,
      confirmAction: null,
      isDangerous: false,
      icon: null,
    },
    notificationPopup: { active: false, title: null, notification: null, type: null },
    timeSelector: {
      startDate: datetimeToDate(today()),
      endDate: datetimeToDate(today()),
      // Granularity is always derived from the date range (no manual selector).
      granularity: autoGranularity(datetimeToDate(today()), datetimeToDate(today())),
      horizon: TIME_HORIZONS[3],
      isExpanded: true,
    },
    preferences: {
      communityListViewMode: "grid", // 'grid' or 'list'
      themeMode: "light", // 'light' or 'dark'
      density: "balanced",
      sidebarStyle: "branded",
      communityListSort: { field: null, dir: "asc" },
    },
  },
  reducers: {
    openSuccessAlert: (state, action) => {
      const { title, description } = action.payload;
      state.successAlert.active = true;
      state.successAlert.title = title;
      state.successAlert.description = description;
    },
    closeSuccessAlert: (state) => {
      state.successAlert.active = false;
      state.successAlert.title = null;
      state.successAlert.description = null;
    },
    openWarningAlert: (state, action) => {
      const { title, text, confirmText, confirmAction, isDangerous, icon } = action.payload;
      state.warningAlert.active = true;
      state.warningAlert.title = title || null;
      state.warningAlert.text = text || null;
      state.warningAlert.confirmText = confirmText;
      state.warningAlert.confirmAction = confirmAction;
      state.warningAlert.isDangerous = isDangerous || false;
      state.warningAlert.icon = icon || null;
    },
    closeWarningAlert: (state) => {
      state.warningAlert.active = false;
      state.warningAlert.title = null;
      state.warningAlert.text = null;
      state.warningAlert.confirmText = null;
      state.warningAlert.confirmAction = null;
      state.warningAlert.isDangerous = false;
      state.warningAlert.icon = null;
    },
    openNotificationPopup: (state, action) => {
      const { title, notification, type } = action.payload;
      state.notificationPopup.active = true;
      state.notificationPopup.title = title;
      state.notificationPopup.notification = notification;
      state.notificationPopup.type = type;
    },
    closeNotificationPopup: (state) => {
      state.notificationPopup.active = false;
      state.notificationPopup.title = null;
      state.notificationPopup.notification = null;
      state.notificationPopup.type = null;
    },
    updateTimeSelector: (state, action) => {
      state.timeSelector.startDate = action.payload.startDate;
      state.timeSelector.endDate = action.payload.endDate;
      // Granularity is never chosen manually: always derive it from the range
      // so every view returns data at a sensible default resolution.
      state.timeSelector.granularity = autoGranularity(
        action.payload.startDate,
        action.payload.endDate
      );
      state.timeSelector.horizon = action.payload.horizon;
      if (action.payload.isExpanded !== undefined) {
        state.timeSelector.isExpanded = action.payload.isExpanded;
      }
    },
    setCommunityListViewMode: (state, action) => {
      if (!state.preferences) {
        state.preferences = {};
      }
      state.preferences.communityListViewMode = action.payload;
    },
    setThemeMode: (state, action) => {
      if (!state.preferences) {
        state.preferences = {};
      }
      state.preferences.themeMode = action.payload;
    },
    setDensity: (state, action) => {
      state.preferences.density = action.payload;
    },
    setSidebarStyle: (state, action) => {
      state.preferences.sidebarStyle = action.payload;
    },
    setCommunityListSort: (state, action) => {
      if (!state.preferences) {
        state.preferences = {};
      }
      state.preferences.communityListSort = action.payload;
    },
  },
});

export const {
  openSuccessAlert,
  closeSuccessAlert,
  openWarningAlert,
  closeWarningAlert,
  openNotificationPopup,
  closeNotificationPopup,
  updateTimeSelector,
  setCommunityListViewMode,
  setThemeMode,
  setDensity,
  setSidebarStyle,
  setCommunityListSort,
} = applicationSlice.actions;

export const selectSuccessAlert = (state) => state.application.successAlert;
export const selectWarningAlert = (state) => state.application.warningAlert;
export const selectNotificationPopup = (state) => state.application.notificationPopup;
export const selectTimeSelector = (state) => state.application.timeSelector;
export const selectCommunityListViewMode = (state) =>
  state.application.preferences?.communityListViewMode || "grid";
export const selectThemeMode = (state) => state.application.preferences?.themeMode || "light";
export const selectDensity = (state) => state.application.preferences?.density || "balanced";
export const selectSidebarStyle = (state) =>
  state.application.preferences?.sidebarStyle || "branded";
export const selectCommunityListSort = (state) =>
  state.application.preferences?.communityListSort ?? { field: null, dir: "asc" };
export default applicationSlice.reducer;
